import { combineReducers, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { uniqBy } from "lodash";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { apiRequest } from "services/ServiceUtil";
import { updatePlanAsync } from "../MyPlanDetails/MyPlanDetails.slice";
import { PlanState } from "./plans";

const SLICE = "MyPlan";

const initialState: PlanState = {
  list: [],
  totalRecords: 0,
  status: "idle",
  brands: [],
  adminInncodes: [],
  ownerDetails: [],
  myPlanShowLoader: false,
  selectedAdminInncodes: [],
  innCodeDetails: [],
  isActionHighlight: false
};

export const fetchOwnerDetailsAsync = createAsyncThunk(`${SLICE}/fetchOwnerDetailsAsync`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], { size: data.size, hltIds: data.value })
);

export const fetchMyPlansAsync = createAsyncThunk(`${SLICE}/fetchMyPlansAsync`, (data: {}, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getPlans, {}, thunkApi, [], data)
);

export const deleteMyPlansAsync = createAsyncThunk(`${SLICE}/deleteMyPlansAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.deletePlans, data.params, thunkApi, [data.id])
);

export const fetchAdminInncodesAsync = createAsyncThunk(`${SLICE}/fetchAdminInncodesAsync`, (data: {}, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getInncode, {}, thunkApi, [], { searchText: data })
);

export const MyPlansSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    clearAdminInncodes: (state) => {
      state.adminInncodes = [];
    },
    setInncodeDetails: (state, action) => {
      state.innCodeDetails = action.payload;
    },
    clearMyPlanList: (state) => {
      state.list = [];
    },
    setSelectedAdminInncodes: (state, action) => {
      state.selectedAdminInncodes = action.payload;
    },
    setIsPlanHighlight: (state, action) => {
      state.isActionHighlight = action.payload;
    },
    setShowLoader: (state, action) => {
      state.myPlanShowLoader = action.payload;
    },
    setMyPlanSlice: (state, action) => {
      state.brands = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlansAsync.pending, (state, action: any) => {
        state.myPlanShowLoader = action.meta.arg.page === 0;
        state.status = "loading";
      })
      .addCase(fetchMyPlansAsync.fulfilled, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.status = "idle";
        state.list =
          action.meta.arg.page === 0 ? action.payload.userplans : [...state.list, ...action.payload.userplans];
        state.totalRecords = action.payload.meta?.page?.total;
        state.brands = uniqBy(
          action.payload.meta?.filters
            .map((item: any) => {
              return { key: item.brandCode, value: item.brandName };
            })
            .filter((element: any) => !!element?.value)
            .sort((a: any, b: any) => a?.value.localeCompare(b?.value)),
          "key"
        );
      })
      .addCase(fetchMyPlansAsync.rejected, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.status = "idle";
      })
      .addCase(fetchOwnerDetailsAsync.fulfilled, (state, action: any) => {
        state.myPlanShowLoader = false;
        state.ownerDetails = action.payload?.users;
      })
      .addCase(fetchAdminInncodesAsync.fulfilled, (state, action: any) => {
        state.status = "idle";
        state.adminInncodes = action.payload.properties;
      })
      .addCase(deleteMyPlansAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteMyPlansAsync.fulfilled, (state, action: any) => {
        state.status = "idle";
        state.list = state.list.filter((plan) => plan.id !== action?.meta?.arg?.id);
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: any) => {
        const list = [...state.list];
        state.list = list.map((element) => {
          if (element.id === action.payload.id) {
            element = { ...element, ...action.payload, title: action.payload.name };
          }
          return element;
        });
      });
  }
});

const rootReducer = combineReducers({
  MyPlans: MyPlansSlice.reducer
});

export const {
  clearAdminInncodes,
  clearMyPlanList,
  setSelectedAdminInncodes,
  setInncodeDetails,
  setIsPlanHighlight,
  setShowLoader,
  setMyPlanSlice
} = MyPlansSlice.actions;

export default rootReducer;
